import React from "react";
import { Card, CardContent, Box, Typography, Skeleton } from "@mui/material";
import { Wrench } from "@mynaui/icons-react";

const RepairCostsByCategory = ({ data, loading, fixedHeight }) => {
  const {
    costBreakdown = [],
    totalPlannedCosts = 0,
    currency = "USD",
  } = data ?? {};
  const hasData = costBreakdown.length > 0;
  const totalForBar = costBreakdown.reduce((sum, cat) => sum + cat.cost, 0);

  return (
    <Card sx={{ height: fixedHeight || "auto" }}>
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 3 }}>
          <Wrench size={24} color="#020618" strokeWidth={1.5} />
          <Typography
            sx={{
              fontWeight: 500,
              fontSize: "18px",
              lineHeight: "28px",
              color: "#111827",
            }}
          >
            Service Costs by Category
          </Typography>
        </Box>

        {loading ? (
          <Box>
            <Skeleton
              variant="rounded"
              height={16}
              sx={{ mb: 3, borderRadius: 2 }}
            />
            {[...Array(4)].map((_, i) => (
              <Box
                key={i}
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    flex: 1,
                  }}
                >
                  <Skeleton variant="circular" width={10} height={10} />
                  <Skeleton width={120} height={16} />
                </Box>
                <Box sx={{ display: "flex", gap: 2 }}>
                  <Skeleton width={60} height={16} />
                  <Skeleton width={60} height={16} />
                </Box>
              </Box>
            ))}
            <Box
              sx={{
                mt: 3,
                pt: 2,
                borderTop: "1px solid",
                borderColor: "divider",
                display: "flex",
                justifyContent: "space-between",
              }}
            >
              <Skeleton width={140} height={20} />
              <Skeleton width={80} height={28} />
            </Box>
          </Box>
        ) : !hasData ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: 180,
            }}
          >
            <Typography variant="body2" sx={{ color: "text.disabled" }}>
              No data available
            </Typography>
          </Box>
        ) : (
          <>
            {/* Stacked Bar */}
            <Box
              sx={{
                display: "flex",
                height: 12,
                borderRadius: 2,
                overflow: "hidden",
                mb: 3,
              }}
            >
              {costBreakdown.map((cat) => (
                <Box
                  key={cat.category}
                  sx={{
                    width:
                      totalForBar > 0
                        ? `${(cat.cost / totalForBar) * 100}%`
                        : "0%",
                    bgcolor: cat.colorCode,
                    transition: "width 0.3s",
                  }}
                />
              ))}
            </Box>

            {/* Column Headers */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 2,
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 400,
                  fontSize: "14px",
                  lineHeight: "20px",
                  color: "#0A0A0A",
                  flex: 1,
                }}
              >
                Cost Breakdown
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 400,
                    fontSize: "14px",
                    lineHeight: "20px",
                    color: "#0A0A0A",
                    minWidth: 60,
                    textAlign: "right",
                  }}
                >
                  Cost ({currency})
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: "text.secondary",
                    fontSize: "0.8125rem",
                    fontWeight: 500,
                    minWidth: 60,
                    textAlign: "right",
                  }}
                >
                  Change (%)
                </Typography>
              </Box>
            </Box>

            {/* Category Rows */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {costBreakdown.map((cat) => (
                <Box
                  key={cat.category}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      flex: 1,
                    }}
                  >
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: "4px",
                        bgcolor: cat.colorCode,
                        flexShrink: 0,
                      }}
                    />
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 400,
                        fontSize: "14px",
                        lineHeight: "20px",
                        color: "#0A0A0A",
                      }}
                    >
                      {cat.category}
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Typography
                      sx={{
                        fontWeight: 600,
                        fontSize: "0.875rem",
                        color: "text.primary",
                        minWidth: 60,
                        textAlign: "center",
                      }}
                    >
                      {cat.cost.toLocaleString()}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: "0.8125rem",
                        minWidth: 60,
                        textAlign: "right",
                        color:
                          cat.changePercentage === "N/A"
                            ? "text.secondary"
                            : cat.changePercentage.startsWith("-")
                              ? "error.main"
                              : "success.main",
                      }}
                    >
                      {cat.changePercentage}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>

            {/* Total */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mt: 3,
                pt: 2,
                borderTop: "1px solid",
                borderColor: "divider",
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 500,
                  fontSize: "14px",
                  lineHeight: "20px",
                  color: "#0A0A0A",
                }}
              >
                Total Planned Costs
              </Typography>
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: "18px",
                  lineHeight: "28px",
                  color: "#0A0A0A",
                }}
              >
                ${totalPlannedCosts.toLocaleString()} {currency}
              </Typography>
            </Box>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default RepairCostsByCategory;
